import { CoachFunderRow, CoachFunderSource, CoachFunderTheme } from "@/services/walter-assistant";
import { FundingEnvelopeUnit } from "@/types/content/funding_basket";

/**
 * Helpers du tableau financeur : formatage des montants/heures et
 * construction des liens vers le catalogue CRM.
 *
 * Routes (alignées sur `src/routes/content`) :
 * - santé  : `/catalogue/sante/<profession>/<theme_id>?funding_organism=<code>&product=<code>`
 * - généraliste : `/catalogue/generaliste/<theme_id>?funding_organism=<code>&product=<code>`
 *
 * Le `domain` du thème vaut `sante` ou `learning` (alias de `generaliste`).
 * Pour le généraliste le segment `:profession` est absent de la route.
 */

const EUR_FORMATTER = new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0
});

export function formatEnvelopeAmount(value: number | null | undefined, unit: FundingEnvelopeUnit): string {
    if (value === null || value === undefined || Number.isNaN(value)) return "—";
    if (unit === "hours") return `${Math.round(value)} h`;
    return EUR_FORMATTER.format(value);
}

export function formatConsumption(value: number | null | undefined, unit: FundingEnvelopeUnit): string | null {
    if (value === null || value === undefined || Number.isNaN(value)) return null;
    return unit === "hours" ? `${Math.round(value)} h` : EUR_FORMATTER.format(value);
}

/**
 * Libellé court de l'origine de la jauge.
 * Seul "non éligible" mérite un badge visible ; les autres sources sont
 * portées par la colonne enveloppe (valeur + barre).
 */
export function funderSourceLabel(source: CoachFunderSource): string | null {
    if (source === "not_eligible") return "Non éligible";
    return null;
}

/**
 * Résout le catalogue CRM depuis `sub_table` (champ fiable : `sante` ou
 * `generaliste`) ou, en repli, depuis `domain` (`sante` / `learning`).
 *
 * sub_table = "sante"       → `/catalogue/sante/<profession>/<id>`
 * sub_table = "generaliste" → `/catalogue/generaliste/<id>`  (pas de profession)
 */
function resolveCatalogBase(subTable: string, domain: string): { urlDomain: string; hasProfession: boolean } | null {
    const key = (subTable || domain || "").trim().toLowerCase();
    if (key === "sante") return { urlDomain: "sante", hasProfession: true };
    if (key === "generaliste" || key === "learning") return { urlDomain: "generaliste", hasProfession: false };
    return null;
}

/**
 * Construit l'URL de détail d'un thème avec deep-link vers la variante phare.
 *
 * Quand ``theme_id`` est connu, l'URL pointe directement vers la fiche thème.
 * Quand il est null (thème synthétique), on utilise la route ``by-product``
 * qui résout dynamiquement le thème à partir du product_code.
 *
 * Exemples :
 * - `/catalogue/sante/sage_femme/58?funding_organism=DPC&product=DPC-P1`
 * - `/catalogue/sante/infirmier/by-product/BSI_3?funding_organism=DPC`
 * - `/catalogue/generaliste/72?funding_organism=CPF&product=CPF-P1`
 * - `/catalogue/generaliste/by-product/LSF_1?funding_organism=CPF`
 */
export function buildThemeDetailUrl(theme: CoachFunderTheme, professionCode: string, fundingCode: string): string | null {
    const resolved = resolveCatalogBase(theme.sub_table, theme.domain);
    if (!resolved) return null;

    const { urlDomain, hasProfession } = resolved;
    const profession = (professionCode || "").trim();
    if (hasProfession && !profession) return null;

    const foQuery = fundingCode ? `?funding_organism=${fundingCode}` : "";
    const productCode = theme.top_product?.product_code;

    if (theme.theme_id !== null && theme.theme_id !== undefined) {
        // Direct link to theme detail page.
        const qs = new URLSearchParams();
        if (fundingCode) qs.set("funding_organism", fundingCode);
        if (productCode) qs.set("product", productCode);
        const query = qs.toString();
        const base = hasProfession
            ? `/catalogue/${urlDomain}/${profession}/${theme.theme_id}`
            : `/catalogue/${urlDomain}/${theme.theme_id}`;
        return query ? `${base}?${query}` : base;
    }

    // Fallback: resolve via product_code at navigation time.
    if (!productCode) return null;
    return hasProfession
        ? `/catalogue/${urlDomain}/${profession}/by-product/${productCode}${foQuery}`
        : `/catalogue/${urlDomain}/by-product/${productCode}${foQuery}`;
}

export function buildFunderListingUrl(row: CoachFunderRow, professionCode: string): string | null {
    const first = row.themes[0];
    if (!first) return null;
    const resolved = resolveCatalogBase(first.sub_table, first.domain);
    if (!resolved) return null;
    const { urlDomain, hasProfession } = resolved;
    const profession = (professionCode || "").trim();
    const qs = new URLSearchParams();
    if (hasProfession && profession) qs.set("professions", profession);
    if (row.funding_code) qs.set("funding_organisms", row.funding_code);
    const query = qs.toString();
    return `/catalogue/${urlDomain}${query ? `?${query}` : ""}`;
}
